import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Calendar, Euro, User, Phone, Mail } from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { motion } from "framer-motion";

/**
 * Erfolgsseite nach abgeschlossener Buchungsanfrage
 * Zeigt eine Zusammenfassung der Buchungsdaten und nächste Schritte
 * Verwendet Animationen für eine ansprechende Darstellung
 * 
 * @param {Object} props - Komponenten-Props
 * @param {Object} props.bookingResponse - API-Response der Buchungsanfrage
 * @param {Object} props.originalFormData - Originale Formulardaten für Anzeige
 * @param {Function} props.onNewBooking - Callback für neue Buchungen
 */
export default function BookingSuccess({ bookingResponse, originalFormData, onNewBooking }) {
  // Extrahiere Daten aus der API-Response
  const bookingData = bookingResponse.data;
  const bookingId = bookingData.booking_id;
  const status = bookingData.status;
  const estimatedPrice = bookingData.estimated_total_price;
  const venue = bookingData.venue;
  const bookingDetails = bookingData.booking_details;
  
  // Fallback für den Fall, dass Daten nicht vorhanden sind
  if (!bookingData || !originalFormData) {
    return (
      <section className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="border-none shadow-2xl">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-semibold text-red-600 mb-4">Fehler beim Laden der Buchungsdaten</h2>
              <p className="text-gray-600 mb-6">Die Buchungsdaten konnten nicht geladen werden.</p>
              <Button onClick={onNewBooking} variant="outline">
                Neue Buchung starten
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }
  return (
    <section className="py-20 bg-gradient-to-br from-green-50 to-white">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="border-none shadow-2xl">
            {/* Erfolgs-Header mit Icon und Nachricht */}
            <CardHeader className="text-center pb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mx-auto mb-6"
              >
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
              </motion.div>
              
              <CardTitle className="text-3xl md:text-4xl font-light mb-4" style={{ color: 'var(--primary)' }}>
                Buchungsanfrage <span className="font-semibold">gesendet!</span>
              </CardTitle>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Vielen Dank für Ihre Anfrage. Wir werden uns schnellstmöglich bei Ihnen melden 
                und die Verfügbarkeit prüfen.
              </p>
            </CardHeader>

            <CardContent className="space-y-8">
              {/* Buchungsdetails in zwei Spalten */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Gewählter Termin */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--primary)' }}>
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-lg">Gewählter Termin</h3>
                    </div>
                    
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Zeitraum:</strong> {format(parseISO(bookingDetails.start_datetime), 'dd.MM.yyyy', { locale: de })} - {format(parseISO(bookingDetails.end_datetime), 'dd.MM.yyyy', { locale: de })}</p>
                      <p><strong>Uhrzeit:</strong> {format(parseISO(bookingDetails.start_datetime), 'HH:mm', { locale: de })} - {format(parseISO(bookingDetails.end_datetime), 'HH:mm', { locale: de })} Uhr</p>
                      <p><strong>Zweck:</strong> {originalFormData.event_purpose}</p>
                      <p><strong>Buchungstyp:</strong> {bookingDetails.type === 'association' ? 'Verein' : 'Privat'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Preisberechnung */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--accent)' }}>
                        <Euro className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-lg">Preisberechnung</h3>
                    </div>
                    
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Geschätzter Gesamtpreis:</strong> {estimatedPrice}€</p>
                      <p><strong>Buchungstyp:</strong> {bookingDetails.type === 'association' ? 'Verein (ermäßigt)' : 'Privat'}</p>
                      <p><strong>Geschirr:</strong> {originalFormData.needs_dishes ? 'Ja (+30€)' : 'Nein'}</p>
                      <p><strong>Status:</strong> <span className="capitalize">{status === 'pending' ? 'Ausstehend' : status}</span></p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Kontaktdaten des Kunden */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg">Ihre Kontaktdaten</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 text-gray-700">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{originalFormData.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span>{originalFormData.customer_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{originalFormData.customer_phone}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Buchungs-ID und Venue-Info */}
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4" style={{ color: 'var(--primary)' }}>
                    Ihre Buchungsanfrage
                  </h3>
                  
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-center justify-between">
                      <span><strong>Buchungs-ID:</strong></span>
                      <span className="font-mono text-lg font-bold" style={{ color: 'var(--primary)' }}>#{bookingId}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span><strong>Veranstaltungsort:</strong></span>
                      <span>{venue.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span><strong>Status:</strong></span>
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        {status === 'pending' ? 'Ausstehend' : status}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Nächste Schritte und Prozessablauf */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4" style={{ color: 'var(--primary)' }}>
                    Wie geht es weiter?
                  </h3>
                  
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                      <p>Wir prüfen die Verfügbarkeit des gewünschten Termins</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                      <p>Sie erhalten innerhalb von 3 Werktagen eine Rückmeldung per E-Mail</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                      <p>Bei Bestätigung erhalten Sie weitere Details zur Buchung und Zahlung per E-Mail</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-4 bg-blue-100 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Hinweis:</strong> Bitte notieren Sie sich Ihre Buchungs-ID #{bookingId} für spätere Anfragen.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Aktions-Buttons und Kontaktinformationen */}
              <div className="text-center space-y-4">
                <Button 
                  onClick={onNewBooking}
                  size="lg"
                  variant="outline"
                  className="px-8"
                >
                  Weitere Buchung anfragen
                </Button>
                
                <p className="text-sm text-gray-500">
                  Bei Fragen erreichen Sie uns unter +49 36259 5600 oder info@bad-tabarz.de
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}